import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AuthLayout } from "../components/auth-layout";
import { Button } from "../components/ui/button";
import { FieldError, Input, Label } from "../components/ui/input";
import { ApiError, api, unwrap } from "../lib/api";
import { authClient } from "../lib/auth";

export const Route = createFileRoute("/invite/$id")({ component: AcceptInvitation });

/** Invitation acceptance: the only way to get an account (invite-only sign-up). */
function AcceptInvitation() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const q = useQuery({
    queryKey: ["invitation", id],
    queryFn: () => unwrap(api.invitations[":id"].$get({ param: { id } })),
  });

  if (q.isPending) return <AuthLayout title="Invitation">Loading…</AuthLayout>;
  if (q.isError) return <AuthLayout title="Invitation">This invitation doesn't exist.</AuthLayout>;
  const { invitation, hasAccount } = q.data;
  if (invitation.status !== "pending" || invitation.expired) {
    return (
      <AuthLayout title="Invitation">
        <p className="text-sm">
          This invitation is {invitation.expired ? "expired" : invitation.status}. Ask for a new
          one.
        </p>
      </AuthLayout>
    );
  }

  const finish = async () => {
    qc.clear();
    await navigate({ to: "/" });
  };

  return (
    <AuthLayout
      title={`Join ${invitation.organizationName}`}
      description={`You were invited as ${invitation.role} (${invitation.email}).`}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          const form = new FormData(e.currentTarget);
          const password = String(form.get("password"));
          setPending(true);
          setError(undefined);
          try {
            if (!hasAccount) {
              await unwrap(
                api.invitations[":id"]["accept-new"].$post({
                  param: { id },
                  json: { name: String(form.get("name")), password },
                }),
              );
            }
            const { error } = await authClient.signIn.email({ email: invitation.email, password });
            if (error) throw new Error(error.message ?? "Sign-in failed");
            if (hasAccount) await unwrap(api.invitations[":id"].accept.$post({ param: { id } }));
            await finish();
          } catch (err) {
            setError(
              err instanceof ApiError || err instanceof Error
                ? err.message
                : "Something went wrong",
            );
          } finally {
            setPending(false);
          }
        }}
      >
        {!hasAccount && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="name">Your name</Label>
            <Input id="name" name="name" autoComplete="name" required />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <Label htmlFor="password">
            {hasAccount ? "Your password" : "Choose a password (min. 10 characters)"}
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            minLength={hasAccount ? undefined : 10}
            autoComplete={hasAccount ? "current-password" : "new-password"}
            required
          />
        </div>
        <FieldError>{error}</FieldError>
        <Button type="submit" disabled={pending}>
          {hasAccount ? "Sign in and join" : "Create account and join"}
        </Button>
      </form>
    </AuthLayout>
  );
}
